/**
 * TrainingScheduleSelector — Gap A do plano Keto Fusion.
 *
 * Grid semanal (Seg-Dom) onde o utilizador escolhe prospetivamente
 * quais os dias de treino desta semana. A cada toggle grava via
 * `POST /api/programs/{program}/training-schedule` e o motor de macros
 * recalcula automaticamente os carbos/refeed dos dias afetados.
 *
 * Fires an implicit Spirit nudge no backend quando o schedule muda
 * (already handled inside the endpoint).
 */

import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, Dumbbell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ScheduleImpactModal from "@/components/progression/ScheduleImpactModal";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const WEEK_LABELS = ["S", "T", "Q", "Q", "S", "S", "D"];
const WEEK_FULL = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

function isoFor(mondayDate, offset) {
  const d = new Date(mondayDate);
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

function weekMonday(today = new Date()) {
  const d = new Date(today);
  const dow = d.getDay(); // 0=Sun … 6=Sat
  const diff = (dow + 6) % 7; // days since Monday
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export default function TrainingScheduleSelector({ program = "keto_fusion", onSaved }) {
  const monday = useMemo(() => weekMonday(), []);
  const isoWeek = useMemo(() => Array.from({ length: 7 }, (_, i) => isoFor(monday, i)), [monday]);
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const [selected, setSelected] = useState(new Set());
  const [logged, setLogged] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [impact, setImpact] = useState(null);

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/programs/${program}/macros/week`, { headers: authHeaders() });
      const data = await res.json();
      const days = data?.days || [];
      const planned = new Set();
      const done = new Set();
      days.forEach((d) => {
        if (d.training_source === "planned") planned.add(d.date);
        else if (d.training_source === "logged") { planned.add(d.date); done.add(d.date); }
      });
      setSelected(planned);
      setLogged(done);
    } catch {
      toast.error("Erro ao carregar semana");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [program]);

  const toggle = (iso) => {
    if (logged.has(iso)) {
      toast.info("Este dia já foi treinado — não podes desmarcar.");
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(iso)) next.delete(iso); else next.add(iso);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/programs/${program}/training-schedule`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ dates: Array.from(selected).sort() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Falhou");
      if (data.changed) {
        toast.success(`Semana atualizada · ${data.count} dias de treino`);
        onSaved?.(data);
        if (data.impact) setImpact(data.impact);
      } else {
        toast.info("Sem alterações");
      }
      await load();
    } catch (e) {
      toast.error(e.message || "Falhou gravar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 rounded-2xl bg-zinc-900/70 border border-zinc-800 flex items-center gap-3 text-zinc-400">
        <Loader2 className="w-4 h-4 animate-spin" /> A carregar semana…
      </div>
    );
  }

  const count = selected.size;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="p-5 rounded-2xl border relative overflow-hidden"
      style={{
        background: "linear-gradient(140deg, rgba(132,204,22,0.10) 0%, rgba(9,9,11,0.95) 60%)",
        borderColor: "rgba(132,204,22,0.35)",
        boxShadow: "0 6px 22px rgba(132,204,22,0.15), inset 0 1px 0 rgba(132,204,22,0.2)",
      }}
      data-testid="training-schedule-selector"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-lime-500/15 border border-lime-500/40 flex items-center justify-center">
            <CalendarDays className="w-4 h-4 text-lime-400" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-lime-400">Esta semana</p>
            <p className="text-sm font-black text-zinc-100 leading-tight">Marca os teus dias de treino</p>
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">
          {count} / 7
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5 mb-4">
        {isoWeek.map((iso, i) => {
          const isSelected = selected.has(iso);
          const isLogged = logged.has(iso);
          const isToday = iso === todayIso;
          return (
            <button
              key={iso}
              onClick={() => toggle(iso)}
              disabled={isLogged || saving}
              className="relative aspect-square rounded-xl border transition-all active:scale-90 disabled:cursor-not-allowed"
              style={{
                borderColor: isSelected ? "#84cc16" : (isToday ? "#525252" : "#27272a"),
                background: isSelected
                  ? (isLogged ? "rgba(16,185,129,0.30)" : "rgba(132,204,22,0.22)")
                  : "rgba(24,24,27,0.7)",
                boxShadow: isSelected ? "0 0 14px rgba(132,204,22,0.4)" : "none",
              }}
              data-testid={`schedule-day-${i}`}
              title={WEEK_FULL[i] + (isLogged ? " · treinado" : "")}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-[9px] font-bold text-zinc-500 leading-none">{WEEK_LABELS[i]}</span>
                <span className={`text-base font-black leading-none mt-0.5 ${isSelected ? "text-lime-300" : "text-zinc-300"}`}>
                  {parseInt(iso.slice(-2), 10)}
                </span>
                {isSelected && (
                  <Dumbbell className="w-2.5 h-2.5 text-lime-400 absolute bottom-1 right-1" />
                )}
                {isLogged && (
                  <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-emerald-400" title="Treinado" />
                )}
                {isToday && !isSelected && (
                  <span className="absolute bottom-1 left-1 w-1 h-1 rounded-full bg-zinc-500" title="Hoje" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] text-zinc-500 leading-snug flex-1">
          Ponto verde = dia já treinado (bloqueado).<br />
          Os macros dos dias marcados sobem em carbs pós-treino.
        </p>
        <button
          onClick={save}
          disabled={saving}
          className="px-4 h-9 rounded-lg text-black text-xs font-bold whitespace-nowrap active:scale-95 disabled:opacity-50"
          style={{
            background: "linear-gradient(135deg, #84cc16, #10b981)",
            boxShadow: "0 4px 14px rgba(132,204,22,0.4)",
          }}
          data-testid="schedule-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gravar semana"}
        </button>
      </div>

      <ScheduleImpactModal
        open={!!impact}
        impact={impact}
        onClose={() => setImpact(null)}
      />
    </motion.div>
  );
}
